import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, type User } from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { setDoc, serverTimestamp, getDoc } from 'firebase/firestore'
import { userRef } from '@/lib/firestore/collections'

interface AuthCtx {
  user: User | null
  loading: boolean
  error: string | null
  signIn: (email: string, password: string) => Promise<void>
  signOutUser: () => Promise<void>
}

const Ctx = createContext<AuthCtx | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    let upserted = false

    // Sincroniza user + loading assim que a persistência carrega.
    // Evita race: antes, `setLoading(false)` rolava antes do upsert async
    // do `onAuthStateChanged` chamar `setUser`, o que abria uma janela
    // em que o AppShell via `loading=false, user=null` e mandava pro /login.
    void auth.authStateReady().then(() => {
      if (cancelled) return
      setUser(auth.currentUser)
      setLoading(false)
    })

    async function upsertIfNeeded(u: User) {
      if (upserted) return
      upserted = true
      try {
        const ref = userRef(u.uid)
        const snap = await getDoc(ref)
        if (!snap.exists()) {
          const baseDoc: Record<string, unknown> = {
            uid: u.uid,
            email: u.email ?? '',
            displayName: u.displayName ?? '',
            createdAt: serverTimestamp(),
            settings: {
              theme: 'light',
              defaultBoardType: 'whiteboard',
              autosaveIntervalMs: 1500,
              versionsToKeep: 50,
            },
          }
          if (u.photoURL) baseDoc.photoURL = u.photoURL
          await setDoc(ref, baseDoc as never)
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[auth] falha no upsert do user doc:', e)
      }
    }

    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u)
      setError(null)
      if (!u) {
        upserted = false
        return
      }
      if (import.meta.env.DEV) {
        const token = await u.getIdTokenResult()
        // eslint-disable-next-line no-console
        console.info('[auth]', u.email, '· tenant=', auth.tenantId, '· claims.firebase=', token.claims.firebase)
      }
      void upsertIfNeeded(u)
    })

    return () => {
      cancelled = true
      unsub()
    }
  }, [])

  async function signIn(email: string, password: string) {
    setError(null)
    try {
      await signInWithEmailAndPassword(auth, email, password)
    } catch (e) {
      const code = (e as { code?: string }).code ?? ''
      if (code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')) {
        setError('Credenciais inválidas. Verifique e-mail e senha.')
      } else {
        setError('Falha no login. Tente novamente.')
      }
      throw e
    }
  }

  async function signOutUser() {
    await signOut(auth)
  }

  return <Ctx.Provider value={{ user, loading, error, signIn, signOutUser }}>{children}</Ctx.Provider>
}

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be inside <AuthProvider>')
  return ctx
}
